/* eslint-disable no-case-declarations */
import qs, { stringify } from 'qs';
import CustomSkinHelper from '@/pages/product/components/CustomSkin/helper';
import { cloneDeep, has } from 'lodash';
import { _8kLivePid } from './configs/8kLive.config';
import Cache from './libs/Cache';
import InstaStorage from './libs/InstaStorage';
import emojiRegex from './libs/emojiRegex';
import { armsErrorSend } from './libs/armsErrorSend';
import { Commodity } from '@/types/product';
import { CommoditySaleState } from '@/types/state';
import { TPageCommonConfig } from '@/types/server';
import { IGuestProfile, IUserProfile } from '@/types/auth';

const isBroswer = typeof window !== 'undefined';

/**
 * 获取cache实例
 * @param  {null}
 * @return {Object} cacheInstance
 */
let cacheInstance: Cache | null = null;

function getCache() {
  if (cacheInstance) {
    return cacheInstance;
  } else {
    cacheInstance = new Cache();
    return cacheInstance;
  }
}
const cache = getCache();

/*
 *
 * @desc: 判断是否为移动端屏幕
 * @param null
 * @return {boolen} value
 * @time: 2019-06-03 18:36
 *
 */
function isMobile() {
  let isMobile = true;
  if (typeof window !== 'undefined') {
    isMobile = window.__NEXT_DATA__.props.pageData.runtimeInject.isMobile;
  }
  if (typeof window !== 'undefined' && window.screen.width > 768) {
    isMobile = false;
  }
  if (typeof document !== 'undefined' && document && document.body.clientWidth > 768) {
    isMobile = false;
  }
  return isMobile;
}

/*
 *
 * @desc: 判断是否微信浏览器环境
 * @param null
 * @return null
 * @time: 2019-06-03 17:55
 *
 */
function isWechat() {
  return typeof window !== 'undefined' ? /micromessenger/.test(navigator.userAgent.toLowerCase()) : false;
}

/**
 *
 * @desc: 获取url参数值
 * @param {string} variable
 * @return {string} value
 * @time: 2019-06-03 18:12
 *
 */
function getQueryVariable(variable: string) {
  const query = typeof window !== 'undefined' ? window.location.search.substring(1) : '';
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return undefined;
}

/*
 *
 * @desc: 获取当前语言（服务端渲染不可使用）
 * @param null
 * @return {string} value
 * @time: 2019-06-03 18:12
 *
 */
function getLang(): X_Language {
  const defaultLanguage = 'en_us';
  try {
    if (isBroswer) {
      const globalLanguage = window?.country?.language?.toLowerCase() as X_Language;
      const cacheLanguage = cache.getCookie('X_Language')?.toLowerCase() as X_Language;
      return globalLanguage ?? cacheLanguage ?? defaultLanguage;
    }
    return defaultLanguage;
  } catch (e) {
    armsErrorSend(e as Error, { filename: 'getLang' });
    return defaultLanguage;
  }
}
const X_Language = getLang();

let LANG = {} as LANG;
function getLANG(): LANG {
  try {
    if (isBroswer) {
      LANG = window.__NEXT_DATA__.props.pageData.runtimeInject.localeData || {};
      return LANG;
    }
    return {};
  } catch (e) {
    armsErrorSend(e as Error, { filename: 'getLANG' });
    return {};
  }
}
/**
 *
 * @description: 获取当前国家
 * @return {String} value
 * @time: 2019-06-03 18:12
 *
 */
function getCountryCode(country?: TBasicCountryInfo) {
  const defaultCountryCode = country?.code || 'US';
  try {
    if (isBroswer) {
      const globalCountryCode = window?.country?.code;
      const cacheCountryCode = cache.getCookie('X_Country_Code');
      return globalCountryCode ?? cacheCountryCode;
    }
    return defaultCountryCode;
  } catch (e) {
    console.error(e);
    return defaultCountryCode;
  }
}
const CountryCode = getCountryCode();

/**
 *
 * @desc: 获取当前货币
 * @param {TBasicCountryInfo} country 服务器端获取到当前Currency 信息
 * @return {string} value
 * @time: 2019-06-03 18:12
 *
 */
function getCurrency(country?: TBasicCountryInfo) {
  const defaultCountryCurrency = country?.currency || 'USD';
  try {
    if (isBroswer) {
      const globalCountryCurrency = window?.country?.currency;
      const cacheCountryCurrency = cache.getCookie('X_Currency');
      return globalCountryCurrency ?? cacheCountryCurrency;
    }

    return defaultCountryCurrency;
  } catch (e) {
    console.error(e);
    return defaultCountryCurrency;
  }
}

/**
 *
 * @desc: 获取当前货币符号
 * @param {TBasicCountryInfo} country 服务器端获取到当前Signal 信息
 * @return {string} value
 * @time: 2020-11-20
 *
 */
function getSignal(country?: TBasicCountryInfo) {
  const defaultCountryCurrencySignal = country?.currency_signal || '$';
  try {
    if (isBroswer) {
      const globalCountryCurrencySignal = window?.country?.currency_signal;
      const cacheCountryCurrencySignal = cache.getCookie('X_Currency_Signal');
      return globalCountryCurrencySignal ?? cacheCountryCurrencySignal;
    }

    return defaultCountryCurrencySignal;
  } catch (e) {
    console.error(e);
    return defaultCountryCurrencySignal;
  }
}
const CountrySignal = getSignal();

/**
 * 金额浮点数精度
 * @param  {number} num
 * @param {number} flag 小数点位数
 * @return {number} result
 */
function formatPrice(num: number, flag = 2) {
  if (num % 1 === 0) {
    return num;
  } else {
    num *= 1;
    const times = Math.pow(10, flag);
    const res = parseInt((num > 0 ? num * times + 0.5 : num * times - 0.5) as unknown as string, 10) / times;
    return Number(res.toFixed(flag));
  }
}

/**
 *
 * @desc: 去除字符串空格
 * @param {string} str
 * @return {string} str
 * @time: 2019-06-03 17:38
 *
 */

function strTrim(str: string) {
  return str && typeof str === 'string' ? str.replace(/\s/g, '') : '';
}
/**
 * 正则提取传入富文本去掉标签后的内部文字，可用于判断是否为空
 */
function getRichText(richText: string) {
  if (!richText) return '';
  // 可用于匹配 <img/>、<audio></audio>、<video></video>的标签及内容
  // const reg1 = new RegExp('<(img|audio|video)(S*)[^>]*>', 'gi');
  const reg2 = new RegExp('<[^>]+>', 'gi');
  const text = richText?.replace(reg2, '') ?? '';

  return text || '';
}

const podEnv = typeof window === 'undefined' ? '' : window.__NEXT_DATA__?.props?.pageData?.appInject?.PodEnv ?? '';

/**
 * 判断传入数据是否为空 为空时isEmpty() = true
 * @param strictFlag 是否严格比对，传入true时string会将空格、richText的空标签也视为空
 * @description 空对象/0/bool 判断为空，非0数字非空
 */
function isEmpty(value: unknown, strictFlag = false) {
  try {
    if (!value || typeof value === 'boolean') {
      return true;
    }

    if (Array.isArray(value) || (!strictFlag && typeof value === 'string')) {
      return value.length === 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    if (strictFlag && typeof value === 'string') {
      const _value = getRichText(value);
      return _value.replace(/\s*/g, '').length === 0;
    }

    return false;
  } catch (e) {
    console.error(e);
    return false;
  }
}

/**
 *
 * @desc: 处理产品名
 * @param {string} _name
 * @param {string} type
 * @return {string}
 * @time: 2020/1/6 14:18
 *
 */
type GetNameFormatType =
  /** 括号前的产品名 */
  | 'split'
  /** 产品名和括号后的名称中间插入<br/>换行 */
  | 'newline'
  /** 产品名和括号后的名称中间插入<br/>换行，并且过滤名称中的品牌词 */
  | 'newlineAndFilterInsta360'
  /** 只要括号前的产品名，并且过滤品牌词 */
  | 'simple'
  /** 没指定格式，只过滤空格 */
  | undefined;
function getName(name: string, type: GetNameFormatType) {
  if (isEmpty(name)) return '';
  name = name?.replace('（', '(');
  name = name?.replace('）', ')');
  const splitIndex = name.lastIndexOf('(');
  switch (type) {
    case 'split':
      name = name.split('(')[0];
      break;
    case 'newline':
      if (splitIndex >= 0) {
        name = name.slice(0, splitIndex) + '<br/>' + name.slice(splitIndex);
      }
      break;
    case 'newlineAndFilterInsta360':
      if (splitIndex >= 0) {
        name = name.slice(0, splitIndex) + '<br/>' + name.slice(splitIndex);
      }
      name = name.replace('Insta360', '').replace('影石', '');
      break;
    case 'simple':
      if (!name.toLowerCase().includes('care')) {
        name = name.split('(')[0];
        name = name.replace('Insta360', '').replace('影石', '');
      }
      break;
    default:
  }
  return name.trim();
}

/**
 *
 * 格式化产品名
 * @param {string} name 产品名
 * @param {boolean} isAllVersionSplit 是否返回所有的versionname; versionName.map()
 * @returns {object} data 格式化对象
 *
 * - data.split 根据括号进行分割，取括号前面的内容；
 * - data.newline 根据最后一个括号字符进行分割，对内容进行换行操作；页面需要使用样式控制换行符换行；
 * - data.newlineAndFilterInsta360 根据最后一个括号字符进行分割，对内容进行换行操作，并对Insta360/影石关键字进行移除;
 * - data.simple 根据括号进行分割，取括号前面的内容，并对Insta360/影石关键字进行移除;
 * - data.versionSplit 根据 || 进行分割，返回[规格名，套餐名]，如果没有规格则返回[套餐名，套餐名]
 */
function formatProductName(name: string, isAllVersionSplit = false) {
  name = name ? name?.replace(/（/g, '(').replace(/）/g, ')') : '';
  const splitIndex = name?.lastIndexOf?.('(');
  const data = {
    name,
    splitIndex,
    split: '',
    newline: '',
    newlineAndFilterInsta360: '',
    simple: '',
    versionSplit: [''],
  };

  if (isEmpty(name)) return data;

  const split = (val: string) => {
    return val.split('(')[0];
  };
  const newline = (val: string) => {
    val = splitIndex <= 0 ? val : `${val.slice(0, splitIndex)}\n${val.slice(splitIndex)}`;
    return val;
  };
  const filterInsta360 = (val: string) => {
    return val.replace('Insta360', '').replace('影石', '');
  };
  const handleVersionName = (name: string) => {
    if (!name) return ['', ''];
    const arr = name.split('||');
    if (arr.length === 1) {
      return [arr[0].trim(), arr[0].trim()];
    } else {
      return isAllVersionSplit ? arr.map((o) => o.trim()) : [arr[0].trim(), arr[arr.length - 1].trim()];
    }
  };
  data.split = split(name).trim();
  data.newline = newline(name).trim();
  data.newlineAndFilterInsta360 = filterInsta360(newline(name)).trim();
  data.versionSplit = handleVersionName(name);

  const hasFilterProduct = name.toLowerCase().includes('care');
  data.simple = hasFilterProduct ? name.trim() : filterInsta360(split(name)).trim();

  return data;
}

/**
 * 传入套餐名/产品名，如果重复返回true
 * @param {string} pname 产品名
 * @param {string} cname 套餐名
 * @param {object} type 默认判断include相等，可传 equal使用===判断，除此外传入 split等 调用getName(xx,type)判断
 */
function isSameProductName(pname: string, cname: string, type: GetNameFormatType | 'equal') {
  if (typeof pname !== 'string' || typeof cname !== 'string') return false;

  let _pname = pname.replace(/\s/g, '').toLowerCase();
  let _cname = cname.replace(/\s/g, '').toLowerCase();
  if (type && type !== 'equal') {
    _pname = getName(_pname, type);
    _cname = getName(_cname, type);
  }

  return type === 'equal' ? _pname === _cname : _pname.includes(_cname) || _cname.includes(_pname);
}

/**
 * 比对字符串是否相等或包含
 * @param {string} str1
 * @param {string} str2
 * @param {string} method 比对方式，默认 include“包含” | equal 小写全等
 */
function isSameStr(str1: string, str2: string, method = 'include') {
  const _str1 = str1?.replace(/\s/g, '').toLowerCase();
  const _str2 = str2?.replace(/\s/g, '').toLowerCase();

  return method === 'equal' ? _str1 === _str2 : _str1.includes(_str2) || _str2.includes(_str1);
}

/*
 *
 * @desc: 获取元素距离文档左上角的位置坐标
 * @param {Object} el
 * @return {type} value
 * @time: 2019-06-03 17:23
 *
 */
function getEleOffset(el: HTMLElement) {
  let t = el.offsetTop;
  let l = el.offsetLeft;
  let eleParent = el.offsetParent as HTMLElement; // 不考虑为null的情况，我们选中的文本节点除了html根节点都是有父节点的
  while (eleParent !== null) {
    l += eleParent.offsetLeft;
    t += eleParent.offsetTop;
    eleParent = eleParent.offsetParent as HTMLElement;
  }
  return {
    x: l,
    y: t,
  };
}

/*
 *
 * @desc: 获取元素距离文档顶部的距离
 * @param {object} el
 * @return {number} value
 * @time: 2019-06-03 17:09
 *
 */
function getEleTop(el: HTMLElement) {
  let elActualTop = el.offsetTop;
  let elParent = el.offsetParent as HTMLElement;
  while (elParent !== null) {
    elActualTop += elParent.offsetTop;
    elParent = elParent.offsetParent as HTMLElement;
  }
  return elActualTop;
}

/*
 *
 * @desc: 判断item销售状态
 * @param {Object} commodity
 * @return {boolen} value
 * @time: 2019-06-10 14:30
 *
 */
function isAvailableForSale(
  commodity: Partial<Prettify<Commodity>> & { sale_state: CommoditySaleState; stock: number }
) {
  if (!commodity) {
    return false;
  }
  const { sale_state, stock } = commodity;
  return !(stock <= 0 || sale_state < 0);
}

/*
 *
 * @desc: 页面路由跳转
 * @param {string} url
 * @return null
 * @time: 2019-06-10 15:59
 *
 */
function browserPushUrl(url: string) {
  if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}

/**
 *
 * @desc:
 * @param {type} params
 * @return {HTMLElement} dom 如果服务端渲染会返回undefined！
 * @time: 2019-06-20 11:21
 *
 */
function querySelector(dom: string, time = 100): Promise<Element> {
  return new Promise((resolve, reject) => {
    let counter = 0;
    const MAX_COUNTER = 50;
    if (typeof document === 'undefined') return;
    const timer = setInterval(() => {
      const DOM = document.querySelector(dom);
      counter++;
      if (DOM) {
        resolve(DOM);
        clearInterval(timer);
      }
      if (counter > MAX_COUNTER) {
        reject('querySelector timeout');
        clearInterval(timer);
      }
    }, time);
  });
}

/**
 *
 * @desc: 在指定元素中查询DOM节点
 * @param {HTMLElement | ShadowRoot} ele
 * @param {string} dom
 * @return {Promise<Element | undefined>} dom 如果服务端渲染会返回undefined！
 * @time: 2019-06-20 11:21
 *
 */
function querySelectorInElement(ele: HTMLElement | ShadowRoot, dom: string): Promise<Element | undefined> {
  return new Promise((resolve) => {
    let counter = 0;
    const MAX_COUNTER = 50;
    if (typeof ele === 'undefined') return;
    const timer = setInterval(() => {
      const DOM = ele.querySelector(dom);
      counter++;
      if (DOM) {
        resolve(DOM);
        clearInterval(timer);
      }
      if (counter > MAX_COUNTER) {
        clearInterval(timer);
      }
    }, 100);
  });
}

/**
 * 页面触底判断
 * @param margin 触底函数判断范围
 */
function isReachBottom(margin = 36) {
  // 窗口高度，兼容处理
  const windowHeight = document.documentElement.clientHeight || document.body.clientHeight;
  // 滚动高度
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
  // 页面高度
  const documentHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
  if (windowHeight + scrollTop + margin > documentHeight) {
    return true;
  } else {
    return false;
  }
}

/**
 * 页面触顶判断
 * @param margin 触顶函数判断范围
 */
function isReachTop(margin = 4) {
  // 滚动高度(滑动弹窗的伪滚动也要算)
  const scrollTop =
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    ((document.body.style.position === 'fixed' &&
      Math.abs(parseFloat(document.body.style.top.replace('px', '')))) as number);
  return scrollTop < margin;
}

/**
 *
 * @desc:
 * @param {type} params
 * @return {HTMLElement} dom 如果服务端渲染会返回undefined！
 * @time: 2019-06-20 11:21
 *
 */
function querySelectorAll(dom: string): Promise<NodeList> {
  return new Promise((resolve) => {
    let counter = 0;
    const MAX_COUNTER = 50;
    if (typeof document === 'undefined') return;
    const timer = setInterval(() => {
      const DOM = document.querySelectorAll(dom);
      counter++;
      if (DOM) {
        resolve(DOM);
        clearInterval(timer);
      }
      if (counter > MAX_COUNTER) {
        clearInterval(timer);
      }
    }, 100);
  });
}

/**
 *
 * @desc:
 * @param {type} params
 * @return {HTMLElement} dom 如果服务端渲染会返回undefined！
 * @time: 2019-06-20 11:21
 *
 */
function querySelectorAllInElement(ele: HTMLElement, dom: string): Promise<NodeList> {
  return new Promise((resolve) => {
    let counter = 0;
    const MAX_COUNTER = 50;
    if (typeof ele === 'undefined') return;
    const timer = setInterval(() => {
      const DOM = ele.querySelectorAll(dom);
      counter++;
      if (DOM) {
        resolve(DOM);
        clearInterval(timer);
      }
      if (counter > MAX_COUNTER) {
        clearInterval(timer);
      }
    }, 100);
  });
}

/**
 * scrollToSmooth
 * @desc:平滑滚动
 * @param {number} top
 * @param {number} left
 * @param {string} behavior 'auto' | 'smooth' | 'instant'
 * @time: 2019-06-27 11:01
 *
 */
function scrollToSmooth(top: number, left = 0, behavior: 'auto' | 'smooth' | 'instant' = 'smooth') {
  try {
    try {
      window.scrollTo({
        top,
        left,
        behavior,
      });
    } catch (e) {
      console.error(e);
      window.scrollTo(left, top);
    }
  } catch (e) {
    armsErrorSend(e as Error, { filename: 'utils window.scrollTo(x,y)' });
  }
}

/**
 * eleScrollToSmooth
 * @desc:dom节点的平滑滚动，内置兼容性处理
 * @param {number} top
 * @param {number} left
 * @param {string} behavior 'auto' | 'smooth' | 'instant'
 *
 */
function eleScrollToSmooth(
  dom: HTMLElement,
  top: number,
  left = 0,
  behavior: 'auto' | 'smooth' | 'instant' = 'smooth'
) {
  try {
    if (typeof dom.scrollTo !== 'function') return;
    try {
      dom.scrollTo({
        top,
        left,
        behavior,
      });
    } catch (e) {
      console.error(e);
      dom.scrollTo(left, top);
    }
  } catch (e) {
    armsErrorSend(e as Error, { filename: 'utils dom.scrollTo(x,y)' });
  }
}

/**
 *
 * @desc: 滚动到指定DOM节点，
 * @param {type} params
 * @return {type} value
 * @time: 2021/7/9 5:08 下午
 *
 */
function scrollToEle(ele: string | HTMLElement, topMargin = 20, behavior: 'auto' | 'smooth' | 'instant' = 'smooth') {
  if (!ele) return;
  const e = (typeof ele === 'string' ? document.querySelector(ele) : ele) as HTMLElement;
  const objOffset = getEleOffset(e);
  const header = document.querySelector('#g-header') as HTMLElement;
  const headerHeight = header?.offsetHeight ?? 0;
  scrollToSmooth(objOffset.y - headerHeight - topMargin, 0, behavior);
}

/** 判断当前商品是否为允许直接购买的商品，逻辑抽离于下面根据商品渲染加车按钮，由于商品状态已经耦合在里面了，改不动，直接抽离一个新的逻辑 */
export function isBuyNowProducts(commodity: Partial<Commodity>) {
  try {
    if (!commodity) return false;
    return commodity?.product === _8kLivePid || commodity?.bind_service;
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * @desc: 匹配销售状态 返回对应按钮的文案
 * @param {LANG} String
 * @param {sale_state} Number
 * @param {stock} Number
 * @return {state} String
 * @time: 2019-06-27 16:25
 *
 */
function fetchSaleStateBtn(commodity: Commodity, _LANG: LANG) {
  const { sale_state, stock, product: pid } = commodity;
  const LANG = _LANG || getLANG();
  let state = null;
  const notifyLabel = LANG['common.btn.notify'];

  if (sale_state === 0) {
    // 选购
    if (stock > 0) {
      state = LANG['product.sale_state.add_to_cart'];

      // 单独购买绑定服务，除定制贴外，显示立即购买文案（直接结账）。
      if (commodity) {
        const is8KLive = pid === _8kLivePid;
        if (is8KLive) return LANG['common.btn.buyNow'];
        if (CustomSkinHelper.CustomSkinPidList.includes(commodity?.product)) return LANG['common.seeMore'];
        if (commodity?.bind_service) return LANG['common.btn.buyNow'];
      }
    } else {
      state = notifyLabel;
    }
  } else if (sale_state === -1 || sale_state === -2) {
    // 缺货/下架
    state = notifyLabel;
  } else if (sale_state === -3) {
    // 即将上架
    state = LANG['product.sale_state.coming_soon'];
  } else if (sale_state === 1) {
    // 预售
    if (stock > 0) {
      state = LANG['product.sale_state.per_sale'];
    } else {
      state = notifyLabel;
    }
  } else if (sale_state === 2) {
    // 预定
    if (stock > 0) {
      state = LANG['product.sale_state.pre_order'];
    } else {
      state = notifyLabel;
    }
  } else {
    state = notifyLabel;
  }
  return state;
}

/*
 *
 * @desc: 获取Youtube字幕语言参数
 * @return {string} cc_lang_pref
 * @time: 2020/3/23 16:37
 *
 */

function getCCLangPref() {
  const YouTuBeLANG = {
    zh_tw: 'zh-Hant',
    en_us: 'en',
    ko_kr: 'ko',
    ja_jp: 'ja',
    de_de: 'de',
    es_es: 'es',
  };
  return YouTuBeLANG[X_Language as keyof typeof YouTuBeLANG] || 'en';
}

/**
 *
 * @desc: 处理返回url，如果只传了key就删掉，如果传了key和value就把当前url添加上query，构成新的url返回
 * @param {key} paramsKey
 * @param {value} paramsValue
 * @return {string} url
 * @time: 2020/4/2 10:14
 *
 */
function handleQueryParams(key: string, value: string) {
  const { origin, pathname, search } = window.location;
  const query = qs.parse(search.substring(1));
  let href = typeof window !== 'undefined' ? window.location.href : '';
  if (key) {
    if (value) {
      query[key] = value;
    } else {
      delete query[key];
    }
    const qsStr = qs.stringify(query);
    href = `${origin}${pathname}${qsStr ? '?' + qsStr : ''}`;
  }
  return href;
}

/**
 *
 * @description 处理url参数, params对象有值则进行新增或替换，若对象属性值为空则移除链接的参数
 * @param {String} url
 * @param {Object} params
 * @return {string} url
 *
 */
function handleUrlParams(url: string, params: Record<string, unknown>) {
  try {
    // 打平url携带参数、外部参数
    const searchParams: Record<string, string> = {};
    const regex = /[?&]([^?&]+)=([^?&]+)/g;
    const _url = `${url}?${qs.stringify(params)}`;
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(_url)) !== null) {
      const key = decodeURIComponent(match[1]);
      const value = decodeURIComponent(match[2]);
      searchParams[key] = value;
      if (key in params && isEmpty(params[key])) delete searchParams[key];
    }

    // 重组url
    const queryString = Object.entries(searchParams)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    url = url.split('?')[0] + '?' + queryString;
    return url;
  } catch (e) {
    console.error(e);
    return url;
  }
}

/**
 *
 * @desc: 获取url参数
 * @param {key} key
 * @return {string} value
 * @time: 2020/4/2 10:14
 *
 */
function fetchQueryParams(key: string) {
  const search = qs.parse(window.location.search.substring(1));
  if (key) return search[key] || '';
  return search;
}

/**
 *
 * @desc: 函数防抖
 * @param {Function} func
 * @param {Number} delay 默认500
 * @return {Function} function
 * @time: 2020/4/11 11:16
 *
 */

function debounce<TFunction extends (...args: unknown[]) => void = (...args: unknown[]) => void>(
  func: TFunction,
  delay: number = 500
) {
  let timeout: NodeJS.Timeout;
  return function (...args: Parameters<TFunction>) {
    const ctx = this as ThisParameterType<TFunction>;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(ctx, args);
    }, delay);
  };
}

/**
 *
 * @desc: 函数节流
 * @param {type} params
 * @param {Number} threshhold 默认500
 */
function throttle<TFunction extends (...args: unknown[]) => void = (...args: unknown[]) => void>(
  fn: TFunction,
  threshhold: number = 500
) {
  let timeout: NodeJS.Timeout;
  let start = +new Date();
  return function (...args: Parameters<TFunction>) {
    const ctx = this as ThisParameterType<TFunction>;
    const curr = +new Date();
    clearTimeout(timeout);
    if (curr - start >= threshhold) {
      fn.apply(ctx, args);
      start = curr;
    } else {
      timeout = setTimeout(() => {
        fn.apply(ctx, args);
      }, threshhold);
    }
  };
}

/**
 *
 * @description 获取链接（正式环境、测试环境），
 * @param {string} secDomain 二级域名
 * @param {string} pathName 跳转路径 相对路径
 * @param {string} redirect 返回链接，默认当前页面，传入相对路径则自动识别链接环境
 * @param {object} query 携带参数
 */
function handleInstaLink(
  secDomain: 'www' | 'store' | '',
  pathName: string,
  _redirect: string | undefined,
  _query: Record<string, string> = {}
) {
  if (typeof window === 'undefined') return '';
  const storeUrlEnv = ['prod', ''].includes(podEnv) ? '' : `-${podEnv}`;

  switch (secDomain) {
    case 'www':
      const currentLinkEnv = ['prod', ''].includes(podEnv) ? '' : '-dev';

      const langMapLink = {
        en_us: '/us',
        zh_cn: '/cn',
        zh_tw: '/hk',
        de_de: '/de',
        ja_jp: '/jp',
        ko_kr: '/kr',
        fr_fr: '/fr',
        es_es: '/es',
        it_it: '/it',
      };

      const redirect =
        typeof _redirect === 'undefined'
          ? window.location.href
          : isEmpty(_redirect)
          ? ''
          : _redirect?.includes('http')
          ? _redirect
          : window.location.href.includes('localhost')
          ? `http://localhost:3000${_redirect}`
          : `https://store${storeUrlEnv}.insta360.com${_redirect}`;

      const query: Record<string, string> = { redirect_url: redirect, ..._query };
      if (!redirect) delete query.redirect_url;

      return `https://www${currentLinkEnv}.insta360.com${langMapLink[X_Language]}${pathName}${
        isEmpty(query) ? '' : '?'
      }${stringify(query)}`;
    case 'store':
      return podEnv === 'local'
        ? `http://localhost:3000${pathName}`
        : `https://store${storeUrlEnv}.insta360.com${pathName}`;
    default:
      return pathName;
  }
}

/*
 *
 * @desc: 重定向官方登录
 * @param {type} params
 * @return {type} value
 * @time: 2020/6/11 12:10
 *
 */
function navigateToLogin(redirectUrl?: string) {
  window.location.href = handleInstaLink('www', '/account/login', redirectUrl || window.location.href);
}

/*
 *
 * @desc: getClassNames
 * @param {array} group
 * @return {array} value
 * @time: 2021/1/25 18:26
 *
 */
function ClassNames(group: string[]) {
  return group.filter((o) => o).join(' ');
}

/**
 *
 * @desc: 信用卡脱敏
 * @author: haifn
 * @time: 2021/7/13 10:18 上午
 *
 */
const getCardNumberFormat = (str: string) => {
  const card_number = strTrim(str || (document.getElementById('card_number') as HTMLInputElement).value);
  return card_number.replace(/(\d{6})\d*(\d{4})/, `$1${new Array(card_number.length - 10).fill('*').join('')}$2`);
};

/**
 * 数组去重
 * @param array object[]
 * @param key 传入则去除数组中key值重复的项
 */
const uniq = <T extends Record<string, unknown> | number | string>(array: Array<T>, key?: string) => {
  if (!Array.isArray(array) || array.length === 0) return array;
  const _array: T[] = [];
  array!.forEach((d) => {
    // 如果是对象则比较是否key值重复，如果是其他值则直接判断不能等于key
    if (typeof d === 'object' && key) {
      !_array.some((o) => (o as Record<string, unknown>)[key] === d[key]) && _array.push(d);
    } else {
      !_array.includes(d) && _array.push(d);
    }
  });
  return _array;
};

/** 检查字符串中是否含有表情字符,日常文字以2字节（16位 一字符）存储，超出的会被拆成两个字符 */
const hasEmoji = (str: string) => {
  if (typeof str !== 'string' || !str) return false;
  return [...str.matchAll(emojiRegex)].length > 0;
};

/**
 *
 * @description 查询某个节点的特定祖先节点
 * @param {Object} ele 当前节点
 * @param {String} type 类型，id || className || attr
 * @param {String} typeValue 值
 * @return {Object} parentEle 祖先节点
 * @time 2022/10/8 16:26
 *
 */
const findParentEle = (ele: HTMLElement, type: 'id' | 'className' | 'attr', typeValue: string) => {
  if (!ele || !type || !typeValue) return null;

  function loop(ele: HTMLElement | undefined | null) {
    if (!ele) return null;
    if (!ele.parentNode) return null;

    const eleParent = ele.parentNode as HTMLElement;
    switch (type) {
      case 'id':
        if (!ele.parentNode.querySelector(`#${typeValue}`)) return loop(eleParent);
        return ele.parentNode;
      case 'className':
        if (!ele.parentNode.querySelector(`.${typeValue}`)) return loop(eleParent);
        return ele.parentNode;
      case 'attr':
        if (!eleParent.getAttribute(`${typeValue}`)) return loop(eleParent);
        return ele.parentNode;
      default:
        return null;
    }
  }

  return loop(ele);
};

/**
 * 获取各国展示时间的格式化类型方式
 */
const getMomentFormat = (code = CountryCode) => {
  let format = 'DD/MM/YYYY';
  switch (true) {
    case ['CN', 'JP', 'KR', 'TW', 'MO'].includes(code):
      format = 'YYYY/MM/DD';
      break;
    case ['US'].includes(code):
      format = 'MM/DD/YYYY';
      break;
    default:
      break;
  }
  return format;
};

/**
 *
 * @description 移动端阻止滚动穿透
 * @param {type} params
 * @return {type} value
 *
 */
const stopTouchMovePreventDefault = (ele: HTMLElement) => {
  if (!ele) return;
  ele.addEventListener('touchmove', (e) => {
    e.preventDefault();
  });
};

/** 返回值为 按顺序排列的数组子项类型 或者promiseReject类型 */
type PromiseAllSettledResult<T> = { [K in keyof T]: T[K] | PromiseRejectedResult };
/**
 * @param {Array} promises Promise数组，失败的场景需要自行catch
 * @param {Object} option  配置选项
 * @param {String} option.filterReject 过滤失败的Promise结果
 *
 * T: 返回的Promise结果类型，是一个数组
 * @returns {Array<TPromiseResultArray[number] | PromiseRejectedResult>} 返回值可能是rejected的Promise结果，注意区分
 */
const PromiseAllSettled = <TPromiseResultArray extends Array<unknown>>(
  promises: Promise<TPromiseResultArray[number]>[] = [],
  option: { filterReject?: boolean } = {}
): Promise<PromiseAllSettledResult<TPromiseResultArray>> => {
  let res = [];
  return new Promise<PromiseAllSettledResult<TPromiseResultArray>>((resolve) => {
    const canAllSettled = !!Promise.allSettled;
    const PromiseAll = canAllSettled ? Promise.allSettled(promises) : Promise.all(promises);

    PromiseAll.then((promiseRes) => {
      res = promiseRes as PromiseResultUnion<TPromiseResultArray[number]>[];
      // promise.all错误时直接抛错，正确时无需处理值，allSettled需要处理值
      if (canAllSettled) {
        if (option?.filterReject) {
          res = promiseRes.filter((o) => (o as PromiseResultUnion<TPromiseResultArray[number]>).status === 'fulfilled');
        }
        res = res.map((o) => (o as PromiseFulfilledResult<TPromiseResultArray[number]>).value || o);
      }

      resolve(res as PromiseAllSettledResult<TPromiseResultArray>);
    });
  });
};

/** pushState时需要replaceState当前路由，fix路由返回bug */
const historyReplaceState = (state = {}, href: string) => {
  if (typeof window !== 'undefined') {
    if (window.history.replaceState) {
      window.history.replaceState({ href: window.location.href, ...state }, '', href || window.location.href);
    }
  }
};

let windowOffsetY = 0;
/**
 *
 * @description 切换页面overflow hidden状态，并设置滚动条偏移，
 *
 *              - 为了解决移动端滑动穿透问题，body设置为fixed，并设置top为当前滚动高度
 *              - 切换状态时派发事件，在部分业务代码里可能需要做某些事情
 *
 * @param { boolean } hideScroll 是否隐藏滚动条
 * @param { object } options 自定义参数 例如：{ hasIntercom: true } 是否展示客服弹窗
 */
const toggleGlobalScroll = (hideScroll: boolean, options: { hasIntercom?: boolean } = {}) => {
  try {
    const scrollBarWidth = window?.innerWidth - document.body.clientWidth;
    const isMobile = window?.innerWidth <= 768; // ipad端 1312
    // 导航栏的偏移
    let pStickyTabBar = null;
    if (scrollBarWidth > 0) {
      pStickyTabBar = document.getElementById('pStickyTabBar');
    }

    if (hideScroll) {
      // 需要同时设置overflow 和padding-right，否则会抖动
      document.body.style.cssText = `overflow: hidden !important; padding-right:${scrollBarWidth}px`;
      document.body.setAttribute('data-HideCustomerService', `${options?.hasIntercom ?? hideScroll}`);

      if (isMobile) {
        windowOffsetY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${windowOffsetY}px`;
      }
    } else {
      document.body.style.cssText = `overflow: auto; padding-right:0px`;
      document.body.setAttribute('data-HideCustomerService', `${options?.hasIntercom ?? hideScroll}`);

      if (isMobile) {
        document.body.style.position = 'static';
        document.body.style.top = `0`;
        scrollToSmooth(windowOffsetY, 0, 'instant');
      }
    }

    if (pStickyTabBar) pStickyTabBar.style.paddingRight = `${pStickyTabBar}px`;
    const toggleGlobalScrollEvent = new CustomEvent('toggleGlobalScroll', { detail: { hideScroll, scrollBarWidth } });
    window.dispatchEvent(toggleGlobalScrollEvent);
  } catch (e) {
    document.body.setAttribute('data-HideCustomerService', 'false');
    armsErrorSend(e as Error, { filename: 'utils toggleGlobalScroll', data: { hideScroll, windowOffsetY } });
  }
};

/*
 *
 * @desc: 判断是否为debug环境，做一些数据打印或其他信息输出
 *
 */
function isDebug() {
  const DEBUG_COOKIE_NAME = '_insta_debug';
  return isBroswer ? cache?.getCookie(DEBUG_COOKIE_NAME) === '1' : false;
}

/**
 * 轮询方法
 * @param {()=>boolean} func 如果成功返回true轮询停止
 * @param {*} delay 轮询间隔
 * @param {*} timer 轮询次数
 * @param {*} payload 方法参数,可以传非数组、数组（多个入参）、或者对象
 * @returns
 */
function intervalPromise<TFunction extends (...args: unknown[]) => boolean>(
  func: (...args: unknown[]) => boolean,
  delay = 1000,
  timer = 10,
  payload?: Parameters<TFunction>
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let counter = 0;
    const t = setInterval(() => {
      try {
        const result = Array.isArray(payload) ? func(...payload) : func();
        if (result) {
          clearInterval(t);
          resolve();
        }
        if (counter >= timer) {
          clearInterval(t);
          reject('intervalPromise 超时');
        }
        counter++;
      } catch (e) {
        armsErrorSend(e as Error, { filename: 'intervalPromise run error', data: { func, delay, timer, payload } });
        clearInterval(t);
        reject('intervalPromise 执行错误');
      }
    }, delay);
  }).catch((e) => {
    console.error(e);
  });
}

/**
 * 是否在优惠弹窗时间范围内
 * @param {} commonConfig 缓存配置
 * @param {string} code 国家代码
 */
function activeSubModalTime(commonConfig: TPageCommonConfig, code: TCountryCode) {
  const active = commonConfig.subscribeModalTime;
  const now = new Date().getTime();
  return active && now >= active[0] && now <= active[1] && code !== 'CN';
}

/** 参数key */
type GetUserAccountKeys = 'id' | 'email' | 'guest' | 'userProfileEmail' | keyof IUserProfile;
/** 返回值类型约束 */
type GetUserAccountReturnType<Key extends GetUserAccountKeys | undefined = undefined> = Key extends
  | 'id'
  | 'email'
  | 'guest'
  | 'userProfileEmail'
  | keyof IUserProfile
  ? string
  : Key extends undefined
  ? IUserProfile
  : '';

/**
 *
 * @description 获取当前用户的account信息
 * @param key 获取具体信息
 */
function getUserAccount<Key extends GetUserAccountKeys | undefined = undefined>(
  key?: Key
): GetUserAccountReturnType<Key> {
  try {
    if (!isBroswer) return (key ? '' : {}) as GetUserAccountReturnType<Key>;
    const userProfile = (InstaStorage.get('user_profile') ?? {}) as IUserProfile;
    const guestProfile = (JSON.parse(window.sessionStorage.getItem('guestProfile') || '{}') ?? {}) as IGuestProfile;

    if (!key) return (userProfile ?? {}) as GetUserAccountReturnType<Key>;
    switch (key) {
      case 'id':
        return String(userProfile?.id ?? '') as GetUserAccountReturnType<Key>;
      case 'email':
        return (userProfile.email ?? guestProfile.email ?? '') as GetUserAccountReturnType<Key>;
      case 'guest':
        return (guestProfile.email ?? '') as GetUserAccountReturnType<Key>;
      case 'userProfileEmail':
        return (userProfile.email ?? '') as GetUserAccountReturnType<Key>;
      default:
        return (userProfile[key as keyof IUserProfile] ?? '') as GetUserAccountReturnType<Key>;
    }
  } catch (e) {
    armsErrorSend(e as Error, { filename: 'utils getUserAccount', data: { key } });
    return (key ? '' : {}) as GetUserAccountReturnType<Key>;
  }
}

/**
 * @description 是否为苹果设备
 * @param {boolean} [isMobile] 不填写默认PC & Mob都校验
 * @returns {boolean}
 */
function isAppleDevice(isMobile = undefined) {
  const pcDevice = /Mac/;
  const mobDevice = /iPhone|iPad|iPod/;
  if (isMobile === undefined) {
    return (
      pcDevice.test(navigator?.platform) ||
      pcDevice.test(navigator?.userAgent) ||
      mobDevice.test(navigator?.platform) ||
      mobDevice.test(navigator?.userAgent)
    );
  }
  return isMobile
    ? mobDevice.test(navigator?.platform) || mobDevice.test(navigator?.userAgent)
    : pcDevice.test(navigator?.platform) || pcDevice.test(navigator?.userAgent);
}

/**
 * @description 是否为safari浏览器
 * @returns {boolean}
 */
function isSafari() {
  const ua = navigator?.userAgent?.toLowerCase();
  const isSafariReg = /^((?!chrome|android).)*safari/i;

  return isSafariReg.test(ua);
}

/** underlineToCamelCase方法返回值的类型安全 */
type ReturnTypeUnderlineToCamelCase<T> = T extends string ? string : T;
/**
 * 下划线转驼峰，对象的所有key转为驼峰
 * @param {string | object} data
 */
function underlineToCamelCase<TUnderlineData extends string | Record<string, unknown>>(data: TUnderlineData) {
  if (isEmpty(data)) return '';
  const changeWord = (str: string) => {
    return str
      .split('_')
      .map((word, index) => {
        // 第一个单词全小写，其它单词首字母大写
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  };

  if (typeof data === 'string') return changeWord(data) as string;
  const _data = cloneDeep(data) as Record<string, unknown>;
  const camelCaseObject = {} as Record<string, unknown>;
  Object.keys(_data).forEach((key) => (camelCaseObject[changeWord(key)] = _data[key]));
  return camelCaseObject as ReturnTypeUnderlineToCamelCase<TUnderlineData>;
}

/**
 * 驼峰转下划线
 */
function camelToUnderline<TCamelCaseData extends string | Record<string, unknown>>(data: TCamelCaseData) {
  if (isEmpty(data)) return '';

  const changeWord = (str: string) => {
    return str.replace(/([A-Z])/g, (match) => {
      return '_' + match.toLowerCase();
    });
  };

  if (typeof data === 'string') return changeWord(data);
  const _data = cloneDeep(data) as Record<string, unknown>;
  const underlineCaseObject: Record<string, unknown> = {};
  Object.keys(_data).forEach((key) => (underlineCaseObject[changeWord(key)] = _data[key]));
  return underlineCaseObject as ReturnTypeUnderlineToCamelCase<TCamelCaseData>;
}

/** 传下划线字段获取 下划线 / 驼峰数据，抹平差异 */
function getCamelUnderlineData(data: Record<PropertyKey, unknown>, underlineKey: string) {
  if (!data) return;
  return data[underlineKey] || data[underlineToCamelCase(underlineKey)];
}

/**
 * 深拷贝合并
 */
function deepCloneAssign<T extends Record<string, unknown>>(origin: Partial<T> = {}, ...args: Partial<T>[]): T {
  for (const i in args) {
    Object.keys(args[i]).forEach((key) => {
      const value = args[i][key as keyof T]; // 要被拷贝的对象中的key值

      if (value && typeof value === 'object') {
        if (!has(origin, key) || Array.isArray(value)) {
          // 如果origin中不存在args[i][key]，直接将args[i][key]复制到origin中，否则在对象内部进行递归深拷贝
          // 如果是一个数组，，也是直接深拷贝而不做
          origin[key as keyof T] = cloneDeep(value) as T[keyof T];
        } else {
          (origin as T)[key as keyof T] = deepCloneAssign({}, origin[key] ?? {}, value) as T[keyof T];
        }
      } else {
        origin[key as keyof T] = value;
      }
    });
  }
  return origin as T;
}

/**
 * @description 校验并返回数组数据
 * @returns {array}
 */
function getArrayData(data: Array<unknown>) {
  return Array.isArray(data) && data.length ? data : [];
}

/**
 * @description 校验并返回对象数据
 * @returns {object}
 */
function getObjectData(data: object) {
  return data && typeof data === 'object' && data !== null ? data : {};
}

/**
 * @description 根据语言获取PushEngage对应的campaign名称
 * @returns {object}
 */
function getPECampaignName(language: X_Language) {
  const campaignNameList = new Map([
    ['en_us', 'default_cart_official'],
    ['zh_tw', 'TC_cart_official'],
    ['ja_jp', 'JP_cart_official'],
  ]);
  // 根据客户端语言获取对应的campaign名称,EN兜底
  return campaignNameList.get(language) || campaignNameList.get('en_us');
}

/**
 * @description 根据Hex颜色值和透明度 获取rgba颜色值
 * @param {string} color 颜色值
 * @param {number} opacity 透明度 0 ~ 100
 * @returns {string} rgba颜色值
 */
const getRgbaColor = (color = '#fff', opacity = 100) => {
  // 验证 HEX 颜色
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)) {
    color = '#fff'; // 默认颜色
  }
  // 处理 HEX 颜色（移除 #）
  let hex = color.replace(/^#/, '');
  // 处理 3 位 HEX（缩写） 转 6 位
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  // 解析 RGB 颜色
  const [r, g, b] = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((c) => parseInt(c, 16));
  // 计算透明度 (0 ~ 1)
  const alpha = Math.min(100, Math.max(0, Number(opacity))) / 100;
  // 返回 RGBA 颜色
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
};

export {
  LANG,
  getLang,
  podEnv,
  X_Language,
  getCountryCode,
  CountryCode,
  getCurrency,
  getSignal,
  getLANG,
  CountrySignal,
  getCache,
  cache,
  isMobile,
  isWechat,
  getQueryVariable,
  getEleOffset,
  formatPrice,
  strTrim,
  getName,
  getEleTop,
  isAvailableForSale,
  browserPushUrl,
  querySelector,
  querySelectorInElement,
  scrollToSmooth,
  eleScrollToSmooth,
  scrollToEle,
  fetchSaleStateBtn,
  getCCLangPref,
  handleQueryParams,
  handleUrlParams,
  fetchQueryParams,
  debounce,
  throttle,
  navigateToLogin,
  ClassNames,
  getRichText,
  isEmpty,
  getCardNumberFormat,
  isBroswer,
  uniq,
  formatProductName,
  isSameProductName,
  isSameStr,
  querySelectorAll,
  querySelectorAllInElement,
  handleInstaLink,
  hasEmoji,
  findParentEle,
  getMomentFormat,
  stopTouchMovePreventDefault,
  PromiseAllSettled,
  isReachBottom,
  historyReplaceState,
  toggleGlobalScroll,
  isDebug,
  isReachTop,
  intervalPromise,
  activeSubModalTime,
  getUserAccount,
  isAppleDevice,
  isSafari,
  underlineToCamelCase,
  camelToUnderline,
  getCamelUnderlineData,
  deepCloneAssign,
  getArrayData,
  getObjectData,
  getPECampaignName,
  getRgbaColor,
};
